// A concrete fact lookup about the current view needs discovery, not intake.
export function isAssistDiscoveryRequest(text) {
  const q = String(text || '').trim();
  if (/\b(?:don't|do not|never|stop|cancel|how do|how to|explain how|she says|he says|if you)\b|["“”]/i.test(q)) return false;
  if (/\b(?:buy|purchase|delete|submit|send|publish|change|edit)\b/i.test(q)) return false;
  const lookup = /\b(?:find|locate|look up|figure out|identify|tell me|what(?:'s| is)|which)\b/i.test(q);
  const fact = /\b(?:address|acreage|acres|parcel|zoning|registrar|domain|website|business name)\b/i.test(q);
  const reference = /\b(?:this|that|current|visible|shown|screen|map|property|parcel|Cloudflare)\b/i.test(q);
  return lookup && fact && reference;
}

// Questions about a visible field need a fresh observation, even without the word screen.
export function isVisibleFieldRequest(text) {
  const q = String(text || '').trim();
  if (/\b(?:do not|don't|don’t|never)\s+(?:look|read|scan|inspect)\b/i.test(q)) return false;
  if (/\b(?:then|and)\s+(?:click|type|change|delete|save|submit|send|buy)\b/i.test(q)) return false;
  const lookup = /^(?:(?:okay|alright|please|aria)[,\s]+)*(?:what|which|can you (?:see|read|tell)|read|look at|scan|tell me|identify)\b/i.test(q);
  const field = /\b(?:domain|website address|registrar|DNS records?|nameservers?|account name|page title|URL)\b/i.test(q);
  const visible = /\b(?:open|current|visible|shown|showing|displayed|this page|Cloudflare|address bar|on (?:the|my|this) screen)\b/i.test(q);
  return lookup && field && visible;
}
// Read-only inspection must not enter the legacy action middleware.
export function isAssistInspectionRequest(text) {
  const q = String(text || '');
  if (isVisibleFieldRequest(q)) return true;
  const inspection = /\b(inspect|compare|comparison|likeness|reference|verify|describe|assess|evaluate|before editing|before changing)\b/i.test(q);
  const readOnly = /\b(?:do not|don't|don’t|without)\s+(?:actually\s+)?(?:change|edit|save|modify|click|type|act|touch)\b/i.test(q)
    || /\b(?:before editing|before changing|read.only|just (?:inspect|compare|describe|assess))\b/i.test(q);
  const request = /^(?:please\s+)?(?:inspect|compare|describe|assess|evaluate|what|how|which|can you (?:inspect|compare|describe|assess))\b/i.test(q.trim());
  const positiveAction = /(?:^|[.!?;]\s*|\bthen\s+|\band\s+)(?:please\s+)?(?:click|type|delete|save|modify|change|edit|move|open|run|submit|buy|send|install)\b/i.test(q);
  return inspection && !positiveAction && (readOnly || request);
}

// A goal request needs grounded conversation, not keyword-triggered commands.
export function isAssistGoalRequest(text) {
  const q = String(text || '');
  const goal = /\b(?:help me|help with|i (?:need|want|would like) (?:help|you to help|to find|to make|to build|one for))\b/i.test(q);
  const directControl = /\b(?:click|press|type|scroll|delete|submit|pay|send|open|launch|install)\b/i.test(q);
  return goal && !directControl;
}

export function parseInAppSearch(text) {
  const q=String(text || '').trim().replace(/^(?:(?:aria|hey|please)[,.!\s]+)+/i,'');
  const leading=q.match(/^(?:(?:in|on|inside)\s+)?(Google Earth|Google Maps|Chrome|Edge|Firefox|Notepad|Excel|Word)[,;:.!\s]+(?:please\s+)?search(?:\s+for)?\s+(.+?)[.!]?$/i);
  const trailing=q.match(/^search(?:\s+for)?\s+(.+?)\s+(?:in|inside)\s+(Google Earth|Google Maps|Chrome|Edge|Firefox|Notepad|Excel|Word)[.!]?$/i);
  const match=leading || trailing;
  if (!match) return null;
  return {app:leading ? match[1] : match[2],query:(leading ? match[2] : match[1]).trim()};
}
export function isInAppSearchRequest(text) {
  return !!parseInAppSearch(text);
}
export function isAssistWorkRequest(text) {
  const q = String(text || '').trim();
  const addressed=q.replace(/^(?:(?:hey|aria|ari|please)[,.!\s]+)+/i,'');
  // Mentioning an action while asking for advice or reporting someone else's
  // words does not instruct this app to execute that action.
  if (/^(?:should|would|why|how|what|when|whether|if)\b/i.test(addressed)
      || /^(?:(?:she|he|they|you)\s+(?:said|says|told|asked)|the (?:assistant|app|agent)\s+(?:said|says|told|asked))\b/i.test(addressed)) return false;
  if (isAssistInspectionRequest(q) || /\b(?:do not|don't|don’t)\s+(?:change|edit|save|touch)|\b(?:explain|teach me|how do|how to|what is|can you hear)\b/i.test(q)) return false;
  const command = q.replace(/^(?:(?:no|okay|ok|alright|all right|hey|aria|ari)[,.!\s]+)+/i, '').replace(/^(?:(?:i (?:want|need)(?: you)? to|you (?:need|have) to|you|please|can you|could you)\s+)+/i, '');
  if (isInAppSearchRequest(command)) return true;
  // Whisper often omits the comma after the application name. Accept only
  // a named application followed by an explicit imperative, not reported speech.
  if (!/["“”]|\b(?:don't|do not|never|shouldn't|why|how|whether|if|said|says)\b/i.test(q)
      && /^(?:in|on|inside)\s+(?:Google Earth|Google Maps|Chrome|Edge|Firefox|Notepad|Excel|Word)[,;:.!\s]+(?:(?:please|can you|could you)\s+)?(?:open|launch|click|press|type|scroll|select|maximi[sz]e|minimi[sz]e|resize|zoom)\b/i.test(command)) return true;
  // Local discovery belongs to the executor, which can list/search files.
  // Keep quoted, hypothetical and negated instructions out of this route.
  if (!/["“”]|\b(?:don't|do not|never|shouldn't|why|how|whether|if|said|says)\b/i.test(q)
      && /^(?:find|locate|look for|search for)\b/i.test(command)
      && /\b(?:files?|folders?|directory|repository|project)\b/i.test(command)) return true;
  if (/^(?:open|launch|click|press|type|scroll|select|maximi[sz]e|minimi[sz]e|resize|zoom|big[ -]screen|full[ -]screen)\b/i.test(command)) return true;
  if (/^make\s+(?:it|this|that|(?:the\s+)?[\w ]{1,60})\s+(?:bigger|full[ -]screen)\b/i.test(command)) return true;
  // A correction may precede an explicit command later in the same utterance.
  // Do not turn quoted instructions, questions or negated controls into actions.
  if (!/["“”]|\b(?:don't|do not|never|shouldn't|why|how|whether|if|said|says)\b/i.test(q)
      && /\b(?:click (?:on )?(?:the|this|that) (?:search bar|button|link|picture)|scroll (?:down|up)|press (?:ctrl|control|enter))\b/i.test(q)) return true;
  if (/^(?:(?:please|can you|could you)\s+)?(?:open|launch|click|press|type|scroll)\b/i.test(q)) return true;
  // Users commonly name the app/page before the command. Preserve the whole
  // task instead of sending these requests to the conversation-only route.
  if (/^(?:in|on)\s+[^,;\n]{1,160}[,;]\s*(?:(?:please|can you|could you)\s+)?(?:open|launch|click|press|type|scroll)\b/i.test(q)) return true;
  return /\b(?:help me|help with|can you|could you|please|i need you to)\b/i.test(q)
    && /\b(?:build|make|create|edit|fix|improve|model|animate|emoji|avatar|eyelids|blender project)\b/i.test(q);
}

export function isAssistResearchRequest(text) {
  const q = String(text || '').trim();
  if (isInAppSearchRequest(q)) return false;
  if (isAssistInspectionRequest(q) || /\b(?:how do|how to|explain|don't search|do not search)\b/i.test(q)) return false;
  // Explicit browser controls stay in the approved desktop action workflow.
  // A search phrase inside that task must not switch it to background research.
  const browserControl = /^(?:(?:please|can you|could you)\s+)?(?:open|launch)\s+(?:Google|Chrome|Edge|Firefox)\b/i.test(q)
    || /^(?:in|on)\s+(?:Google|Chrome|Edge|Firefox)\b[^,;\n]{0,160}[,;]\s*(?:(?:please|can you|could you)\s+)?(?:open|click|press|type|scroll)\b/i.test(q);
  if (browserControl && isAssistWorkRequest(q)) return false;
  // Finding text inside an app is a computer action, not public web research.
  // Otherwise a typing + Ctrl+F task is rejected when research is paused.
  if (/\b(?:ctrl|control)\s*\+\s*f\b|\b(?:find|search)\s+(?:bar|dialog)\b/i.test(q)) return false;
  return /\b(?:find (?:an? |some |the )?(?:option|product|replacement)|search (?:for|online)|look (?:online|for)|research)\b/i.test(q);
}

// Explicit continuation may cross an intervening inspection of the same work,
// but never an unrelated user turn or a no-edit constraint.
export function resolveAssistWorkRequest(text, history = []) {
  const q = String(text || '').trim();
  if (isAssistWorkRequest(q)) return q;
  if (!/^(?:(?:okay|alright|all right|yes|no)[, ]+)?(?:(?:then |please |just )?(?:do it|go ahead|start|fix it)|you decide and do it|your job is to do it)[.!]*$/i.test(q)) return null;
  for (const turn of [...history].reverse().filter(turn => turn.role === 'user').slice(0, 4)) {
    const goal = String(turn.content || '').trim();
    if (/\b(?:do not|don't|don’t|stop|cancel|pause)\b/i.test(goal)) return null;
    if (isAssistWorkRequest(goal)) return `${goal}\nThe user confirmed: ${q}`;
    // These are inspection follow-ups, not new tasks or authorization themselves.
    if (!/\b(?:what (?:does it|do we) need|what needs (?:changing|fixing)|what should (?:we|you) (?:change|fix)|how does it look)\b/i.test(goal)) return null;
  }
  return null;
}

